/**
 * BrainStatusGrid — the heart of L1.
 *
 * Per spec §4.3, this is a 6-tile grid (3×2) — one tile per brain,
 * sorted by `BRAIN_REGISTRY[id].position`. Adding a 7th brain
 * auto-flows to 4×2 or 3×3 with no layout change.
 *
 * Tiles render `BrainSummary` data; the section owns the heading +
 * the grid wrapper.
 */

import type { BrainRegistryEntry } from '../data/brainRegistry';
import type { BrainSummary } from '../data/types';
import { BRAINS_ORDERED } from '../data/brainRegistry';
import { BrainStatusTile } from '../tiles/BrainStatusTile';

// Rendered in place of a BrainStatusTile while the /api/v1/omega/brains
// fetch is still in-flight. Shows "—" for every counter so the page
// never displays false zeros before real data arrives.
function BrainSkeletonTile({ entry }: { entry: BrainRegistryEntry }) {
  const dash = (
    <span className="omega-mono" style={{ color: 'var(--omega-ink-subtle)' }}>
      —
    </span>
  );
  return (
    <div
      style={{
        display: 'block',
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-2)',
        color: 'var(--omega-ink)',
        minHeight: 132,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <span
          style={{
            fontSize: '12pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          {entry.short_name}
        </span>
        <span style={{ flex: 1 }} />
        {/* Muted dot — status unknown while loading */}
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--omega-ink-subtle)',
            display: 'inline-block',
          }}
        />
      </div>
      <div
        style={{
          fontSize: '11pt',
          color: 'var(--omega-ink-muted)',
          letterSpacing: '0.02em',
        }}
      >
        {dash} organs
        <span style={{ margin: '0 8px', color: 'var(--omega-ink-subtle)' }}>·</span>
        {dash} workers
      </div>
      <div style={{ fontSize: '10pt', color: 'var(--omega-ink-muted)', marginTop: 4 }}>
        {dash} decisions / 1m
      </div>
      <div
        style={{
          marginTop: 'var(--omega-gap-2)',
          paddingTop: 'var(--omega-gap-1)',
          borderTop: '1px solid var(--omega-rim)',
          display: 'flex',
          gap: 'var(--omega-gap-2)',
          fontSize: '9pt',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--omega-ink-subtle)',
        }}
      >
        — refusals · — alerts
      </div>
    </div>
  );
}

export function BrainStatusGrid({
  brains,
  isLoading,
}: {
  brains: BrainSummary[];
  isLoading?: boolean;
}) {
  // Index by id so we can render in registry order even when the
  // server returns a partial list.
  const byId = new Map(brains.map((b) => [b.id, b]));

  return (
    <section
      aria-labelledby="omega-brain-grid-heading"
      style={{ marginBottom: 'var(--omega-gap-3)' }}
    >
      <h2
        id="omega-brain-grid-heading"
        style={{
          margin: '0 0 var(--omega-gap-2) 0',
          fontSize: '11pt',
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--omega-ink-muted)',
        }}
      >
        Brains
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 'var(--omega-gap-2)',
        }}
      >
        {BRAINS_ORDERED.map((b) => {
          const summary = byId.get(b.id);
          // While the first fetch is in-flight show skeleton tiles so the
          // page never renders false zeros before real data arrives.
          if (isLoading && !summary) {
            return <BrainSkeletonTile key={b.id} entry={b} />;
          }
          const resolved = summary ?? {
            id: b.id,
            status: 'paused' as const,
            organ_count: 0,
            worker_count: 0,
            decisions_last_min: 0,
            refusals_last_min: 0,
            active_alerts: 0,
            last_activity_iso: null,
            bound_specialists: [],
            bound_triggers: [],
            executions_last_24h: 0,
            resource_usage_mb_current: 0,
          };
          return <BrainStatusTile key={b.id} brain={resolved} />;
        })}
      </div>
    </section>
  );
}
